import { z } from 'zod';
import { itemListQuerySchema } from '@/lib/validation';
import {
  AI_SEARCH_QUERY_LENGTH_MESSAGE,
  AI_SEARCH_QUERY_MAX_LENGTH,
  AI_SEARCH_QUERY_MIN_LENGTH,
} from '@/constants/ai-search';
import type { ItemListQuery } from '@/lib/validation';
import {
  controlledTagTermSchema,
  itemFiltersSchema,
  itemTypeSchema,
  previewViewportsSchema,
  type ItemFilters,
  tailwindVersionSchema,
} from '@/types';
import { defineEndpoint, idPathParamsSchema, type IdPathParams } from './base';

export const rawItemTermSchema = z.object({
  slug: z.string(),
  name: z.string(),
});

export const rawControlledTagTermSchema = controlledTagTermSchema;

export const rawItemListItemSchema = z.object({
  id: z.number().int().positive(),
  slug: z.string(),
  title: z.string(),
  summary: z.string().default(''),
  previewImageUrl: z.string().nullable().optional(),
  previewImageWidth: z.number().int().positive().nullable().optional(),
  previewImageHeight: z.number().int().positive().nullable().optional(),
  savedCount: z.number().int().nonnegative().default(0),
  types: z.array(rawItemTermSchema).default([]),
  uiCategories: z.array(rawItemTermSchema).default([]),
  primaryRole: rawControlledTagTermSchema.nullable().optional().default(null),
  tags: z.array(rawControlledTagTermSchema).default([]),
  searchScore: z.number().optional(),
  tailwindVersion: tailwindVersionSchema.default('v4'),
  featured: z.boolean().default(false),
  updatedAt: z.string().optional(),
});

export const rawItemListResponseSchema = z.object({
  items: z.array(rawItemListItemSchema),
  pagination: z.object({
    page: z.number().int().positive(),
    perPage: z.number().int().positive(),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  }),
});

export const rawItemDetailSchema = rawItemListItemSchema.extend({
  adminEditUrl: z.string().nullable().optional(),
  notes: z.string().default(''),
  code: z.string().default(''),
  previewViewports: previewViewportsSchema.default({
    desktop: true,
    tablet: true,
    mobile: true,
  }),
});

export type RawItemListItem = z.infer<typeof rawItemListItemSchema>;
export type RawItemListResponse = z.infer<typeof rawItemListResponseSchema>;
export type RawItemDetail = z.infer<typeof rawItemDetailSchema>;
export type { ItemListQuery };

const aiSearchQuerySchema = z.object({
  query: z.string()
    .min(AI_SEARCH_QUERY_MIN_LENGTH, AI_SEARCH_QUERY_LENGTH_MESSAGE)
    .max(AI_SEARCH_QUERY_MAX_LENGTH, AI_SEARCH_QUERY_LENGTH_MESSAGE),
  limit: z.number().int().positive().optional(),
  type: z.string().optional(),
  tags: z.string().optional(),
});

const rawAiSearchPlanSchema = z.object({
  contractVersion: z.string().default(''),
  query: z.string().default(''),
  requiredTags: z.array(z.string()).default([]),
  preferredTags: z.array(z.string()).default([]),
  excludedTags: z.array(z.string()).default([]),
  relatedRoles: z.array(z.string()).default([]),
  preferredTypes: z.array(itemTypeSchema).default([]),
  notes: z.array(z.string()).default([]),
});

const rawAiSearchMetaSchema = z.object({
  plannerProviderRequested: z.string().default(''),
  embeddingProviderRequested: z.string().default(''),
  modelRequested: z.string().default(''),
  dimensionsRequested: z.number().int().default(0),
  candidateLimit: z.number().int().nonnegative().optional(),
  candidateCount: z.number().int().nonnegative().default(0),
  eligibleCount: z.number().int().nonnegative().default(0),
  typeConstraintMode: z.string().default('not_requested'),
  queryEmbeddingModels: z.array(z.string()).default([]),
}).passthrough();

export const rawAiSearchResponseSchema = z.object({
  query: z.string(),
  mode: z.enum(['hybrid']),
  plan: rawAiSearchPlanSchema,
  meta: rawAiSearchMetaSchema.default({
    plannerProviderRequested: '',
    embeddingProviderRequested: '',
    modelRequested: '',
    dimensionsRequested: 0,
    candidateCount: 0,
    eligibleCount: 0,
    typeConstraintMode: 'not_requested',
    queryEmbeddingModels: [],
  }),
  notes: z.array(z.string()).default([]),
  items: z.array(rawItemListItemSchema).default([]),
});

export type RawAiSearchResponse = z.infer<typeof rawAiSearchResponseSchema>;
export type AiSearchQuery = z.infer<typeof aiSearchQuerySchema>;

export const ITEM_CONTRACT = {
  list: defineEndpoint<void, RawItemListResponse, void, ItemListQuery>({
    method: 'GET',
    path: '/items',
    auth: 'public',
    schemas: {
      query: itemListQuerySchema,
      response: rawItemListResponseSchema,
    },
  }),
  detail: defineEndpoint<void, RawItemDetail, IdPathParams>({
    method: 'GET',
    path: '/items/:id',
    auth: 'public',
    schemas: {
      pathParams: idPathParamsSchema,
      response: rawItemDetailSchema,
    },
  }),
  updateThumbnail: defineEndpoint<void, RawItemDetail, IdPathParams>({
    method: 'POST',
    path: '/items/:id/thumbnail',
    auth: 'authenticated',
    schemas: {
      pathParams: idPathParamsSchema,
      response: rawItemDetailSchema,
    },
  }),
  filters: defineEndpoint<void, ItemFilters>({
    method: 'GET',
    path: '/filters',
    auth: 'public',
    schemas: {
      response: itemFiltersSchema,
    },
  }),
  aiSearch: defineEndpoint<void, RawAiSearchResponse, void, AiSearchQuery>({
    method: 'GET',
    path: '/ai-search',
    auth: 'public',
    schemas: {
      query: aiSearchQuerySchema,
      response: rawAiSearchResponseSchema,
    },
  }),
} as const;
